import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import MessageHandler from '@sm360/widget-commons-forms';
import { ContentZonesService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { PurchaseMethodsV25, Quote, Vehicle, ThirdPartiesJs } from '@sm360/widget-commons-vehicle';
import { MEDIA_QUERIES, REDIRECTIONS, ShiftDigitalService } from '@sm360/widget-commons-general';
import PaymentCalculatorDefault from '@sm360/widget-payment-calculator-v25';
import { withAnalyticsV25 } from '@sm360/widget-commons-vdp';
import TradeIn360Default from '@sm360/widget-trade-in-360';
import * as Actions from 'vdp-used/actions/vdpUsedActions.js';

import CertifiedSection from 'vdp-used/components/PhotosAndBodyAndCertifiedSection/CertifiedSection.jsx';
import VehicleBanner from 'vdp-used/containers/VehicleBanner/VehicleBanner.js';
import VehicleDescription from 'vdp-used/containers/VehicleDescription/VehicleDescription.js';
import VehicleFeatures from 'vdp-used/containers/VehicleFeatures/VehicleFeatures.js';
import VehicleWarranties from 'vdp-used/components/VehicleWarranties/VehicleWarranties.jsx';
import IdentificationPriceSection from 'vdp-used/containers/VdpSections/IdentificationAndPrice.js';

import VdpForm from 'vdp-used/components/VdpForm/VdpForm.jsx';
import VehicleExtraCta from 'vdp-used/components/VehicleExtraCta/VehicleExtraCta.jsx';
import WarrantiesAndPolicies from 'vdp-used/components/WarrantiesAndPolicies/WarrantiesAndPolicies.jsx';
import Disclaimer from 'vdp-used/components/Disclaimer/Disclaimer.jsx';

import 'vdp-used/components/VdpUsed/VdpUsed.scss';

const { motoInsight } = ThirdPartiesJs;

const VdpUsed = ({
    actions,
    carfaxKey,
    carfaxLeadGenerator,
    certifiedUrl,
    purchaseMethods,
    quote,
    invalidState,
    vehicle,
    loading,
    isShowroomV2Enabled,
    websiteSoldDaysShown,
    cashPurchaseOption,
    config,
    showCarfax,
    hasAdobeTagging,
    displayCreditRate,
    websiteLogo,
    websiteDomain,
    organizationUnitPhone,
    websiteFacebookPageUrl,
    websiteInstagramClientId,
    websiteInstagramUserId,
    websiteTwitterUrl,
    websiteYoutubeUrl,
    organizationUnitName,
    motoInsightUsedEnabled,
    roadsterUsedEnabled,
    eshopUsedEnabled,
    shopClickDriveUsedEnabled,
    roadsterLink,
    dealerCode,
    dealerBAC,
}) => {
    const [hasSDInitialClickSent, setSDInitialClickSend] = useState(false);
    const { PaymentCalculator } = PaymentCalculatorDefault;
    const { TradeInBarWrapper: TradeIn360BarWrapper, TradeInTiles: TradeIn360Tiles } = TradeIn360Default.components;

    const onPaymentCalculatorClose = () => {
        if (Quote.QuoteService.isQuoteInGarageV25(quote.id)) {
            actions.saveToGarage();
        }
    };

    /*
    * Need to send drInitialClick events only once when user interact with DR tool
    * This will send event and set state so it will not send the event again
    */
    const setSDInitialClickEvent = () => {
        ShiftDigitalService.updateCommonEvent(quote?.id, 'drInitialClick', quote?.vehicle);
        setSDInitialClickSend(true);
    };

    const isMobile = useMediaQuery({ maxWidth: MEDIA_QUERIES.MOBILE.MAX });
    const isLaptop = useMediaQuery({ minWidth: MEDIA_QUERIES.LAPTOP.MIN });

    useEffect(
        () => {
            if (vehicle && vehicle.vehicleId && vehicle.vehicleId > 0) {
                // Verify if dateSold is beyond SoldDaysShown parameter, is so, redirect to listing
                if (
                    (vehicle?.isSold() && vehicle.dateSold && !vehicle.isSoldShown(websiteSoldDaysShown)) ||
                    vehicle.isDeleted()
                ) {
                    const windowProxy = WindowProxy.get();
                    const locale = LocaleService.getSimpleLocale();
                    if (windowProxy) {
                        windowProxy.location.href = REDIRECTIONS[locale].listingUsed;
                    }
                }
                if (window?.MOTOINSIGHT) {
                    motoInsight(LocaleService.getLocale().toLowerCase());
                }
            }
        },
        [vehicle?.vehicleId]
    );

    const backgroundImageCZ = ContentZonesService.getContentZone('widget.vdp.used', 'widget.vdp.used.background.img');
    const hasExtraCtas = ContentZonesService.getContentZone('widget.vdp.used.actions.cta', 'has.extra.cta');
    const tradeInDescription = ContentZonesService.getContentZone(
        'widget.vdp.used',
        'widget.vdp.used.trade.in.description'
    );
    const tradeInTitle = ContentZonesService.getContentZone('widget.vdp.used', 'widget.vdp.used.trade.in.title');
    const sectionTitle = ContentZonesService.getContentZone(
        'widget.vdp.used',
        'widget.vdp.used.trade.in.section.title'
    );
    const buyOnlineCta = ContentZonesService.getContentZone(
        'widget.vdp.used.actions.cta',
        'widget.vdp.used.actions.cta.buy.online.text'
    );
    const getPriceCta = ContentZonesService.getContentZone(
        'widget.vdp.used.actions.cta',
        'widget.vdp.used.actions.cta.get.price.text'
    );
    const appointmentCta = ContentZonesService.getContentZone(
        'widget.vdp.used.actions.cta',
        'widget.vdp.used.actions.cta.appointment.text'
    );

    const contentZoneLegalDisclaimer = ContentZonesService.getContentZone(
        'widget.vdp.used',
        'widget.vdp.used.legal.disclaimer'
    );

    const contentZoneBackgroundSummary = ContentZonesService.getContentZone(
        'widget.vdp.used',
        'widget.vdp.used.summary.background.image'
    );

    const legalDisclaimerCz = contentZoneLegalDisclaimer ? contentZoneLegalDisclaimer.getValue() : '';

    const shouldDisplayExtraCta = !invalidState && hasExtraCtas ? hasExtraCtas.getValue() === 'true' : false;

    const backgroundImageStyle = {
        backgroundImage:
            backgroundImageCZ && backgroundImageCZ.getValue() ? `url('${backgroundImageCZ.getValue()}')` : '',
    };

    const backgroundImageSummary = {
        backgroundImage:
            contentZoneBackgroundSummary && contentZoneBackgroundSummary.getValue()
                ? `url('${contentZoneBackgroundSummary.getValue()}')`
                : '',
    };

    const calculator = !invalidState && (
        <PaymentCalculator
            onClose={onPaymentCalculatorClose}
            hasCustomButton
            hasSDInitialClickSent={hasSDInitialClickSent}
            onSetSDInitialClickEvent={setSDInitialClickEvent}
            hasAdobeTagging={hasAdobeTagging}
            config={config}
            displayCreditRate={displayCreditRate}
        />
    );

    const tradeInTileContent = (
        <TradeIn360BarWrapper
            isShowroomV2Enabled={isShowroomV2Enabled}
            style={{ width: '100%' }}
            quote={quote}
            websiteLogo={websiteLogo}
            websiteDomain={websiteDomain}
            organizationUnitPhone={organizationUnitPhone}
            websiteFacebookPageUrl={websiteFacebookPageUrl}
            websiteInstagramClientId={websiteInstagramClientId}
            websiteInstagramUserId={websiteInstagramUserId}
            websiteTwitterUrl={websiteTwitterUrl}
            websiteYoutubeUrl={websiteYoutubeUrl}
            organizationUnitName={organizationUnitName}
            isBarWrapper
        />
    );

    return (
        <div className="showroom-vdp-used">
            {quote.error && (
                <MessageHandler.MessageHandler
                    body={<div>{quote.error}</div>}
                    code="error"
                    messageIcon="icon-Warning"
                    canCloseElement
                />
            )}
            <div className="showroom-vdp-used-inner-content">
                <div className="vdp-used-banner has-secondary-background">
                    <div className="vdp-used-banner-wrapper">
                        <div className="vdp-used-banner--image">
                            <VehicleBanner quote={quote} actions={actions} loading={loading} />
                        </div>
                        <div className="vdp-used-banner--details">
                            <IdentificationPriceSection
                                optionalBuyCta={buyOnlineCta && buyOnlineCta.getValue()}
                                optionalPriceCta={getPriceCta && getPriceCta.getValue()}
                                optionalAppointmentCta={appointmentCta && appointmentCta.getValue()}
                                hasSDInitialClickSent={hasSDInitialClickSent}
                                onSetSDInitialClickEvent={setSDInitialClickEvent}
                                cashPurchaseOption={cashPurchaseOption}
                                config={config}
                                hasAdobeTagging={hasAdobeTagging}
                                vehicle={vehicle}
                                displayCreditRate={displayCreditRate}
                                motoInsightUsedEnabled={motoInsightUsedEnabled}
                                roadsterUsedEnabled={roadsterUsedEnabled}
                                eshopUsedEnabled={eshopUsedEnabled}
                                shopClickDriveUsedEnabled={shopClickDriveUsedEnabled}
                                roadsterLink={roadsterLink}
                                dealerCode={dealerCode}
                                dealerBAC={dealerBAC}
                            />
                        </div>
                    </div>
                    {!invalidState && (
                        <PaymentCalculator
                            onClose={onPaymentCalculatorClose}
                            hasSDInitialClickSent={hasSDInitialClickSent}
                            onSetSDInitialClickEvent={setSDInitialClickEvent}
                            hasAdobeTagging={hasAdobeTagging}
                            vehicle={vehicle}
                            config={config}
                            displayCreditRate={displayCreditRate}
                        />
                    )}
                    {shouldDisplayExtraCta && <VehicleExtraCta vehicle={vehicle} />}
                </div>

                <div>
                    {isMobile && (
                        <>
                            <VehicleDescription />
                            <VehicleWarranties warranty={vehicle?.getWarranty()} />
                            <VehicleFeatures />
                        </>
                    )}
                </div>

                <div className="vdp-used-more-info-wrapper" style={backgroundImageStyle}>
                    <div className="vdp-wrapper-summary">
                        <div className="showroom-vdp-used-about">
                            {isLaptop && (
                                <>
                                    <VehicleDescription />
                                    <VehicleWarranties warranty={vehicle?.getWarranty()} />
                                    <VehicleFeatures />
                                </>
                            )}
                        </div>
                        <div className="vdp-wrapper-more-info">
                            <CertifiedSection
                                certifiedUrl={certifiedUrl}
                                carfaxKey={carfaxKey}
                                carfaxLeadGenerator={carfaxLeadGenerator}
                                vehicle={vehicle}
                                showCarfax={showCarfax}
                            />
                            {isLaptop && <WarrantiesAndPolicies />}
                        </div>
                    </div>
                </div>
                {!(invalidState || vehicle.sold) && (
                    <div className="vdp-used-summary has-secondary-background" style={backgroundImageSummary}>
                        <div>
                            {isLaptop && (
                                <>
                                    <Vehicle.Overview
                                        title={vehicle.getYearMakeModel()}
                                        purchaseMethodsV25={purchaseMethods}
                                        taxCodes={purchaseMethods.getTaxCodesString()}
                                        optionalButton={calculator}
                                        imageUrl={vehicle.multimedia.getMainImage440()}
                                        isReserved={vehicle.onHold}
                                        isSold={vehicle?.isSold()}
                                        sectionTitle={sectionTitle.getValue()}
                                        quote={quote}
                                        optionalBuyCta={
                                            buyOnlineCta ? buyOnlineCta.getValue() : LocaleService.t('BUY_ONLINE')
                                        }
                                        optionalAppointmentCta={appointmentCta && appointmentCta.getValue()}
                                        isUsed
                                        hasSDInitialClickSent={hasSDInitialClickSent}
                                        onSetSDInitialClickEvent={setSDInitialClickEvent}
                                        config={config}
                                        hasAdobeTagging={hasAdobeTagging}
                                        vehicle={vehicle}
                                        displayCreditRate={displayCreditRate}
                                    />
                                    <Disclaimer
                                        legalDisclaimerCz={legalDisclaimerCz}
                                        vehicleDisclaimer={purchaseMethods?.disclaimer}
                                    />
                                    <TradeIn360Tiles
                                        descriptions={tradeInDescription.getValue()}
                                        title={tradeInTitle.getValue()}
                                        quote={quote}
                                        tradeInTileContent={tradeInTileContent}
                                        isShowroomV2Enabled={isShowroomV2Enabled}
                                    />
                                </>
                            )}
                        </div>
                        {isMobile && (
                            <>
                                <TradeIn360Tiles
                                    descriptions={tradeInDescription.getValue()}
                                    title={tradeInTitle.getValue()}
                                    quote={quote}
                                    tradeInTileContent={tradeInTileContent}
                                    isShowroomV2Enabled={isShowroomV2Enabled}
                                />
                                <Disclaimer
                                    legalDisclaimerCz={legalDisclaimerCz}
                                    vehicleDisclaimer={purchaseMethods?.disclaimer}
                                />
                            </>
                        )}
                    </div>
                )}
                <VdpForm vehicleId={vehicle.vehicleId} vehicle={vehicle} config={config} />
            </div>
        </div>
    );
};

VdpUsed.defaultProps = {
    carfaxKey: '',
    carfaxLeadGenerator: false,
    certifiedUrl: '',
    vehicles: [],
    invalidState: false,
    loading: false,
    websiteSoldDaysShown: 0,
    hasAdobeTagging: false,
    displayCreditRate: false,
    motoInsightUsedEnabled: false,
    roadsterUsedEnabled: false,
    eshopUsedEnabled: false,
    shopClickDriveUsedEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

VdpUsed.propTypes = {
    actions: PropTypes.shape({
        loadQuote: PropTypes.func,
        loadVehiclePayments: PropTypes.func,
        loadVehiclesPayments: PropTypes.func,
        setQuoteLead: PropTypes.func.isRequired,
        setVehicle: PropTypes.func,
        saveToGarage: PropTypes.func.isRequired,
    }).isRequired,
    carfaxKey: PropTypes.string,
    carfaxLeadGenerator: PropTypes.bool,
    certifiedUrl: PropTypes.string,
    purchaseMethods: PropTypes.instanceOf(PurchaseMethodsV25.PurchaseMethodsModelV25).isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModelV25).isRequired,
    invalidState: PropTypes.bool,
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModelV25).isRequired,
    loading: PropTypes.bool,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    websiteSoldDaysShown: PropTypes.number,
    config: PropTypes.object.isRequired,
    showCarfax: PropTypes.bool.isRequired,
    hasAdobeTagging: PropTypes.bool,
    displayCreditRate: PropTypes.bool,
    motoInsightUsedEnabled: PropTypes.bool,
    roadsterUsedEnabled: PropTypes.bool,
    eshopUsedEnabled: PropTypes.bool,
    shopClickDriveUsedEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

VdpUsed.displayName = 'VdpUsed';

function mapStateToProps(state) {
    return {
        purchaseMethods: state.purchaseMethodsV25,
        quote: state.quoteV25,
        vehicle: state.vehicleV25,
        vdpUsed: state.vdpUsed,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Actions, dispatch),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withAnalyticsV25(VdpUsed));

export { VdpUsed };
